import { useMemo } from "react";
import type {
  SubscriptionMetadata,
  SubscriptionState,
  SubscriptionStatus,
} from "../types/subscription.types";
import { DEFAULT_SUBSCRIPTION_CONFIG } from "../types/subscription.types";
import type { SubscriptionResponse } from "../repositories/subscription.repository";

/**
 * Custom hook to compute subscription status from subscription data
 *
 * This hook encapsulates the business logic for determining:
 * - Current subscription state
 * - Access permissions
 * - Warning requirements
 * - Grace period calculations
 *
 * @param subscription - Raw subscription data from API
 * @param isLoading - Whether subscription is still loading
 * @param error - Error if subscription fetch failed
 */
export function useSubscriptionStatus(
  subscription: SubscriptionResponse | null | undefined,
  isLoading: boolean,
  error?: unknown
): SubscriptionStatus {
  return useMemo(() => {
    // Loading state
    if (isLoading) {
      return {
        state: "loading",
        metadata: {
          hasAccess: true, // Allow access while loading
          shouldWarn: false,
          shouldBlock: false,
        },
        message: "Carregando informações da assinatura...",
      };
    }

    // Error state
    if (error) {
      return {
        state: "error",
        metadata: {
          hasAccess: true, // Don't block on errors (fail open)
          shouldWarn: true,
          shouldBlock: false,
        },
        message: "Erro ao verificar assinatura. Tente novamente.",
      };
    }

    // No subscription found
    if (!subscription || !subscription.id) {
      return {
        state: "missing",
        metadata: {
          hasAccess: false,
          shouldWarn: false,
          shouldBlock: true,
        },
        message: "Nenhuma assinatura ativa encontrada.",
      };
    }

    const now = new Date();
    const status =
      typeof subscription.status === "string"
        ? subscription.status.toLowerCase()
        : undefined;

    // Parse dates with proper type checking
    const currentPeriodEnd =
      subscription.current_period_end &&
      typeof subscription.current_period_end === "string"
        ? new Date(subscription.current_period_end)
        : undefined;
    const trialEnd =
      subscription.trial_end && typeof subscription.trial_end === "string"
        ? new Date(subscription.trial_end)
        : undefined;

    // Calculate days remaining
    const daysRemaining = currentPeriodEnd
      ? Math.ceil(
          (currentPeriodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        )
      : undefined;

    const config = DEFAULT_SUBSCRIPTION_CONFIG;

    // Determine subscription state
    let state: SubscriptionState = "active";
    const metadata: SubscriptionMetadata = {
      expiresAt: currentPeriodEnd,
      daysRemaining,
      trialEndsAt: trialEnd,
      isTrial: subscription.trial_end ? trialEnd! > now : false,
      hasAccess: true,
      shouldWarn: false,
      shouldBlock: false,
    };
    let message = "Assinatura ativa";

    // Check trial status
    if (metadata.isTrial && trialEnd) {
      const trialDaysRemaining = Math.ceil(
        (trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );
      metadata.daysRemaining = trialDaysRemaining;

      if (trialDaysRemaining <= 0) {
        // Trial expired
        state = "expired";
        metadata.hasAccess = false;
        metadata.shouldBlock = true;
        message = "Período de teste expirado";
      } else if (trialDaysRemaining <= config.criticalWarningDays) {
        state = "trial";
        metadata.shouldWarn = true;
        message = `Seu teste expira em ${trialDaysRemaining} dia${
          trialDaysRemaining !== 1 ? "s" : ""
        }`;
      } else {
        state = "trial";
        message = `Em período de teste (${trialDaysRemaining} dias restantes)`;
      }
      return { state, metadata, message };
    }

    // Check subscription status
    switch (status) {
      case "active":
        state = "active";
        // Check if we should warn about upcoming renewal
        if (
          daysRemaining !== undefined &&
          daysRemaining <= config.warningThresholdDays
        ) {
          metadata.shouldWarn = true;
          if (daysRemaining <= config.criticalWarningDays) {
            message = `Sua assinatura expira em ${daysRemaining} dia${
              daysRemaining !== 1 ? "s" : ""
            }`;
          }
        }
        break;

      case "trialing":
        state = "trial";
        if (
          daysRemaining !== undefined &&
          daysRemaining <= config.criticalWarningDays
        ) {
          metadata.shouldWarn = true;
          message = `Seu teste expira em ${daysRemaining} dia${
            daysRemaining !== 1 ? "s" : ""
          }`;
        }
        break;

      case "past_due":
        state = "past_due";
        metadata.shouldWarn = true;
        metadata.hasAccess = true; // Allow access during retry period
        message = "Pagamento pendente. Atualize sua forma de pagamento.";
        break;

      case "canceled":
      case "cancelled":
        // Check if still in grace period
        if (currentPeriodEnd && currentPeriodEnd > now) {
          state = "grace_period";
          metadata.hasAccess = true;
          metadata.shouldWarn = true;
          metadata.gracePeriodEndsAt = currentPeriodEnd;
          message = `Assinatura cancelada. Acesso até ${currentPeriodEnd.toLocaleDateString(
            "pt-BR"
          )}`;
        } else {
          state = "cancelled";
          metadata.hasAccess = false;
          metadata.shouldBlock = true;
          message = "Assinatura cancelada";
        }
        break;

      case "unpaid":
      case "incomplete":
      case "incomplete_expired":
        state = "expired";
        metadata.hasAccess = false;
        metadata.shouldBlock = true;
        message = "Assinatura inativa. Renovação necessária.";
        break;

      default:
        // Unknown status - check dates
        if (currentPeriodEnd && currentPeriodEnd < now) {
          // Expired but within grace period?
          const daysSinceExpiration = Math.ceil(
            (now.getTime() - currentPeriodEnd.getTime()) / (1000 * 60 * 60 * 24)
          );

          if (daysSinceExpiration <= config.gracePeriodDays) {
            state = "grace_period";
            metadata.hasAccess = true;
            metadata.shouldWarn = true;
            metadata.shouldBlock = false;
            const graceDaysRemaining =
              config.gracePeriodDays - daysSinceExpiration;
            metadata.gracePeriodEndsAt = new Date(
              currentPeriodEnd.getTime() +
                config.gracePeriodDays * 24 * 60 * 60 * 1000
            );
            message = `Período de carência: ${graceDaysRemaining} dia${
              graceDaysRemaining !== 1 ? "s" : ""
            } restante${graceDaysRemaining !== 1 ? "s" : ""}`;
          } else {
            state = "expired";
            metadata.hasAccess = false;
            metadata.shouldBlock = true;
            message = "Assinatura expirada";
          }
        }
    }

    return { state, metadata, message };
  }, [subscription, isLoading, error]);
}
